import React, { useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import getDistance from "geolib/es/getDistance";
import math from "mathjs";
import { getLeafRating } from "../../../util/leafRating";
import { getDisplayableMenuItems } from "../../shared/MenuDisplay";
import ImageLightbox from "../../shared/ImageLightbox";
import { getThumbnailUrl } from "../../../util/imageUtils";
import LeafRating from "../../common/LeafRating";
import RestaurantActionMenu from "../restaurantResults/RestaurantActionMenu";
import { slugify } from "../../../util/slugify";
import useEvent from "../../../hooks/useEvent";
import HighlightText from "../../shared/HighlightText";
import "./ExpandedCard.css";

/**
 * ExpandedCard - Shows detailed restaurant info when a carousel card is tapped
 * Styled to feel like a larger version of the carousel card
 *
 * variant="card" - Used when expanding from carousel (half state) - card-like styling
 * variant="full" - Used when expanding from half list (full state) - takes more space
 */
const ExpandedCardMenuItems = ({ menuItems, searchQuery }) => {
  const [lightbox, setLightbox] = useState(null);

  return (
    <>
      <div className="expanded-card-menu-scroll">
        {menuItems.map((item, index) => {
          const sectionName = item.section?.name || (typeof item.section === 'string' ? item.section : null);
          const thumbUrl = getThumbnailUrl(item.imageUrl);
          return (
            <div key={item._id || index} className={`expanded-card-menu-item${thumbUrl ? ' has-image' : ''}`} style={thumbUrl ? { flexDirection: 'row', gap: '10px', alignItems: 'flex-start' } : undefined}>
              {thumbUrl && (
                <img
                  src={thumbUrl}
                  alt={item.option}
                  onClick={() => setLightbox({ imageUrl: item.imageUrl, name: item.option })}
                  loading="lazy"
                  style={{
                    width: 100, height: 100, borderRadius: 6,
                    objectFit: 'cover', flexShrink: 0, cursor: 'pointer',
                  }}
                />
              )}
              <div style={thumbUrl ? { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' } : undefined}>
                {sectionName && (
                  <span className="expanded-card-menu-item-section">{sectionName}</span>
                )}
                <span className="expanded-card-menu-item-name">
                  <HighlightText text={item.option} query={searchQuery} />
                </span>
                {item.description && (
                  <span className="expanded-card-menu-item-desc">
                    <HighlightText text={item.description} query={searchQuery} />
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {lightbox && (
        <ImageLightbox
          imageUrl={lightbox.imageUrl}
          name={lightbox.name}
          onClose={() => setLightbox(null)}
        />
      )}
    </>
  );
};

const ExpandedCard = ({
  restaurant,
  currentLocation,
  onCollapse,
  variant = "card",
  token,
  searchQuery,
}) => {
  const { event } = useEvent();

  if (!restaurant) {
    return (
      <div className="expanded-card-empty">
        <span>Select a restaurant to see details</span>
      </div>
    );
  }

  // Calculate distance
  const distanceInMeters = getDistance(
    currentLocation,
    restaurant.geoJson.coordinates
  );
  const distance = math.unit(distanceInMeters, "m").to("mi");
  const formattedDistance = math.format(distance, { notation: "fixed", precision: 1 });

  // Get leaf rating
  const rating = getLeafRating(restaurant);

  // Get cuisines
  const cuisines = restaurant.cuisine?.filter(Boolean).map(c =>
    c.replace(/\b\w/g, l => l.toUpperCase())
  ) || [];

  // Get all menu items
  const menuItems = getDisplayableMenuItems(restaurant);

  return (
    <div className={`expanded-card expanded-card-${variant}`}>
      {/* Header */}
      <div className="expanded-card-header">
        <div className="expanded-card-title-section">
          <h2 className="expanded-card-name">
            {restaurant.metadata?.adHocSearch && (
              <span className="expanded-card-adhoc-badge">Discovery</span>
            )}
            {restaurant.name}
          </h2>
          <div className="expanded-card-meta">
            {!event && (
              <>
                <LeafRating count={rating.count} tier={rating.tier} />
                <span className="expanded-card-dot">·</span>
              </>
            )}
            <span className="expanded-card-distance">{formattedDistance}</span>
          </div>
        </div>
        <div className="expanded-card-header-actions">
          {token && (
            <RestaurantActionMenu
              restaurant={restaurant}
              token={token}
            />
          )}
          <button
            className="expanded-card-collapse-btn"
            onClick={onCollapse}
            aria-label="Collapse"
          >
            <i className="fas fa-chevron-down" />
          </button>
        </div>
      </div>

      {/* Cuisines */}
      {cuisines.length > 0 && (
        <div className="expanded-card-cuisines">
          {cuisines.map((cuisine, i) => (
            <span key={i} className="expanded-card-cuisine-tag">{cuisine}</span>
          ))}
        </div>
      )}

      {/* Menu items - horizontal scroll */}
      <div className="expanded-card-menu">
        <div className="expanded-card-menu-header">
          <span className="expanded-card-menu-title">Menu Options</span>
          <span className="expanded-card-menu-count">{menuItems.length} items</span>
        </div>

        {menuItems.length > 0 ? (
          <ExpandedCardMenuItems menuItems={menuItems} searchQuery={searchQuery} />
        ) : (
          <div className="expanded-card-menu-empty">
            No menu items available
          </div>
        )}
      </div>

      {/* Action Link - different for ad hoc vs regular restaurants */}
      {restaurant.metadata?.adHocSearch ? (
        <Link
          to="/ad-hoc/details"
          state={{ restaurant }}
          className="expanded-card-details-link"
        >
          View Full Details
          <i className="fas fa-arrow-right" />
        </Link>
      ) : (
        <Link
          to={`/details/${restaurant._id}/${slugify(restaurant.name)}${searchQuery ? `?keywords=${encodeURIComponent(searchQuery)}` : ""}`}
          className="expanded-card-details-link"
        >
          View Full Details
          <i className="fas fa-arrow-right" />
        </Link>
      )}
    </div>
  );
};

ExpandedCard.propTypes = {
  restaurant: PropTypes.object,
  currentLocation: PropTypes.object.isRequired,
  onCollapse: PropTypes.func,
  variant: PropTypes.oneOf(["card", "full"]),
  token: PropTypes.string,
};

const mapStateToProps = (state) => ({
  token: state.auth.token,
});

export default connect(mapStateToProps)(ExpandedCard);
